import { create } from "zustand";
import { querystring } from "zustand-querystring";
import {
  CustomerListSortType,
  CustomerDeliveryFilterType,
  CustomerPaymentFilterType,
  DateUnits,
  AcquiredViaFilterType,
} from "./CustomerListFilterTypes";

export type CustomerListStoreState = {
  columnSort: CustomerListSortType;
  search: string;
  page: number;
  perPage: number;
  scrollOffset: number;
  priceTier: string;
  availabilityTier: string;
  orderRule: string;
  customerZoneId: string;
  insideSellarDistributionDeliveryArea: boolean;
  deliveryAreaId: string;
  deliveryTier: CustomerDeliveryFilterType;
  paymentTerms: CustomerPaymentFilterType;
  avgOrderMin: string;
  avgOrderMax: string;
  lastOrderedCount: string;
  lastOrderedUnit: DateUnits;
  lastOrderedMoreThan: boolean;
  hasDiscounts: boolean;
  acquiredVia: AcquiredViaFilterType;
};

type CustomerListStoreActions = {
  setState: (state: CustomerListStoreState) => void;

  setColumnSort: (columnSort: CustomerListSortType) => void;
  setSearch: (search: string) => void;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  setScrollOffset: (scrollOffset: number) => void;
  setPriceTier: (priceTier: string) => void;
  setAvailabilityTier: (availabilityTier: string) => void;
  setOrderRule: (orderRule: string) => void;
  setInsideSellarDistributionDeliveryArea: (value: boolean) => void;
  setCustomerZoneId: (customerZoneId: string) => void;
  setDeliveryAreaId: (deliveryAreaId: string) => void;
  setDeliveryTier: (deliveryTier: CustomerDeliveryFilterType) => void;
  setPaymentTerms: (paymentTerms: CustomerPaymentFilterType) => void;
  setAvgOrderMin: (avgOrderMin: string) => void;
  setAvgOrderMax: (avgOrderMax: string) => void;
  setLastOrderedCount: (lastOrderedCount: string) => void;
  setLastOrderedUnit: (lastOrderedUnit: DateUnits) => void;
  setLastOrderedMoreThan: (lastOrderedMoreThan: boolean) => void;
  setHasDiscounts: (hasDiscounts: boolean) => void;
  setAcquiredVia: (acquiredVia: AcquiredViaFilterType) => void;

  reset: () => void;
};

const initialCustomerListStoreState: CustomerListStoreState = {
  columnSort: "name_asc",
  search: "",
  page: 1,
  perPage: 25,
  scrollOffset: 0,
  priceTier: "",
  availabilityTier: "",
  orderRule: "",
  customerZoneId: "",
  insideSellarDistributionDeliveryArea: false,
  deliveryAreaId: "",
  deliveryTier: "all",
  paymentTerms: "customers",
  avgOrderMin: "",
  avgOrderMax: "",
  lastOrderedCount: "",
  lastOrderedUnit: "days",
  lastOrderedMoreThan: true,
  hasDiscounts: false,
  acquiredVia: "all_channels",
};

type CustomerListStoreType = CustomerListStoreState & CustomerListStoreActions;

export const useCustomerListStore = create<CustomerListStoreType>()(
  querystring<CustomerListStoreType>(
    (set) => ({
      ...initialCustomerListStoreState,
      setState: (newState: CustomerListStoreState) => set(newState),

      setColumnSort: (columnSort: CustomerListSortType) => set({ columnSort }),
      setSearch: (search: string) => set({ search }),
      setPage: (page: number) => set({ page }),
      setPerPage: (perPage: number) => set({ perPage }),
      setScrollOffset: (scrollOffset: number) => set({ scrollOffset }),
      setPriceTier: (priceTier: string) => set({ priceTier }),
      setAvailabilityTier: (availabilityTier: string) =>
        set({ availabilityTier }),
      setOrderRule: (orderRule: string) => set({ orderRule }),
      setInsideSellarDistributionDeliveryArea: (value: boolean) =>
        set({ insideSellarDistributionDeliveryArea: value, page: 1 }),
      setCustomerZoneId: (customerZoneId: string) =>
        set({ customerZoneId, page: 1 }),
      setDeliveryAreaId: (deliveryAreaId: string) => set({ deliveryAreaId }),
      setDeliveryTier: (deliveryTier: CustomerDeliveryFilterType) =>
        set({ deliveryTier }),
      setPaymentTerms: (paymentTerms: CustomerPaymentFilterType) =>
        set({ paymentTerms }),
      setAvgOrderMin: (avgOrderMin: string) => set({ avgOrderMin }),
      setAvgOrderMax: (avgOrderMax: string) => set({ avgOrderMax }),
      setLastOrderedCount: (lastOrderedCount: string) =>
        set({ lastOrderedCount }),
      setLastOrderedUnit: (lastOrderedUnit: DateUnits) =>
        set({ lastOrderedUnit }),
      setLastOrderedMoreThan: (lastOrderedMoreThan: boolean) =>
        set({ lastOrderedMoreThan }),
      setHasDiscounts: (hasDiscounts: boolean) => set({ hasDiscounts }),
      setAcquiredVia: (acquiredVia: AcquiredViaFilterType) =>
        set({ acquiredVia }),

      reset: () => set(initialCustomerListStoreState),
    }),
    {
      select(pathname) {
        const persistParam = pathname.includes("/customers");
        return {
          columnSort: persistParam,
          search: persistParam,
          page: persistParam,
          perPage: persistParam,
          scrollOffset: persistParam,
          priceTier: persistParam,
          availabilityTier: persistParam,
          orderRule: persistParam,
          customerZoneId: persistParam,
          insideSellarDistributionDeliveryArea: persistParam,
          deliveryAreaId: persistParam,
          deliveryTier: persistParam,
          paymentTerms: persistParam,
          avgOrderMin: persistParam,
          avgOrderMax: persistParam,
          lastOrderedCount: persistParam,
          lastOrderedUnit: persistParam,
          lastOrderedMoreThan: persistParam,
          hasDiscounts: persistParam,
          acquiredVia: persistParam,
        };
      },
    },
  ),
);
